import { Link } from "react-router-dom";
import { Cpu, Plane, UtensilsCrossed, Newspaper, Trophy, Film, GraduationCap, Briefcase, TreePine, Heart, Users, Wallet, MessagesSquare, ArrowRight } from "lucide-react";
import { getTopics } from "../data/multiLanguage";
import LanguageChip from "../components/LanguageChip";

const ICON_MAP = {
  technology: Cpu,
  travel: Plane,
  "food-culture": UtensilsCrossed,
  "current-events": Newspaper,
  sports: Trophy,
  "movies-entertainment": Film,
  education: GraduationCap,
  "work-career": Briefcase,
  environment: TreePine,
  "health-fitness": Heart,
  "family-relationships": Users,
  "money-finance": Wallet,
};

const GRADIENTS = [
  "from-rust to-rust-dark",
  "from-gold-500 to-gold-600",
  "from-sage to-sage-dark",
  "from-sky to-sky-dark",
  "from-ink-900 to-gold-600",
  "from-gold-500 to-rust-dark",
  "from-gold-500 to-gold-600",
  "from-sage to-sage-dark",
  "from-lime-500 to-sage-dark",
  "from-berry-light0 to-rust-dark",
  "from-gold-500 to-gold-600",
  "from-sky-500 to-sky-dark",
];

export default function Topics() {
  const topics = getTopics();

  return (
    <div className="page-enter pb-12">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MessagesSquare className="w-6 h-6 text-gold-700" />
            <h1 className="text-3xl md:text-4xl font-black bg-gradient-to-r from-gold-500 via-gold-500 to-ink-900 bg-clip-text text-transparent">
              Discussion Topics
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Open-ended chats. The AI asks guiding questions, you reply naturally.
          </p>
        </div>
        <LanguageChip />
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {topics.map((t, idx) => {
          const Icon = ICON_MAP[t.id] || MessagesSquare;
          const grad = GRADIENTS[idx % GRADIENTS.length];
          return (
            <Link
              key={t.id}
              to={`/speak-practice?topic=${t.id}`}
              className="group relative overflow-hidden rounded-2xl bg-white dark:bg-ink-800 border-2 border-gray-100 dark:border-gray-700 hover:border-gold-500 dark:hover:border-gold-500 shadow-sm hover:shadow-xl transition-all hover:-translate-y-1"
            >
              {/* Gradient bar */}
              <div className={`h-2 bg-gradient-to-r ${grad}`} />

              <div className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center text-white shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-transform shadow-md`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-base text-gray-900 dark:text-white group-hover:text-gold-700 dark:group-hover:text-gold-700 transition-colors">
                      {t.title}
                    </h3>
                    <span className={`inline-block mt-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      t.difficulty === "Beginner" ? "bg-sage-light text-sage-dark" :
                      t.difficulty === "Intermediate" ? "bg-cream-100 text-gold-700" :
                      "bg-rust-light text-rust-dark"
                    } dark:bg-opacity-30`}>
                      {t.difficulty}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-3">
                  {t.description}
                </p>

                {/* Guiding questions preview */}
                <div className="space-y-1.5 pt-3 border-t border-gray-100 dark:border-gray-700">
                  {t.guidingQuestions.slice(0, 2).map((q, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-gold-700 mt-0.5 text-[10px] font-bold">{i + 1}.</span>
                      <span className="text-xs text-gray-600 dark:text-gray-300 line-clamp-1 italic">{q}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Start chatting</span>
                  <ArrowRight className="w-4 h-4 text-gold-700 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
